<template>
  <section ref="recent" class="content-group -bg-02 -center -vs-large -hs-medium">
    <div class="group -full">
      <div class="heading-group group -full -flex -between -vs-baseline">
        <div class="heading-sort">
          <h2 class="heading -primary">{{ teamMember.name }}'s Wellness Videos</h2>
          <div class="sorting">
            <div title="Sort Results" class="sort">
              <div class="select-style">
                <form>
                  <select
                    id="sort-wellness"
                    v-model="sortMethod"
                    aria-label="Sort By"
                    class=""
                    @change="fetchWellnessVideos"
                  >
                    <option value="newest">Newest</option>
                    <option value="popular">Most Popular</option>
                  </select>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div class="view-full-list">
          <a :href="`/wellness-videos?teammember%5B%5D=${teamMember.id}&sort=${sortMethod}`"
            >View&nbsp;All Videos</a
          >
        </div>
      </div>

      <!-- error state -->
      <callout v-if="error" type="error">
        <div>{{ error }}</div>
      </callout>

      <!-- loading -->
      <spinner-loader v-if="loading" />

      <!-- DESKTOP VIEW -->
      <div class="group -full desktop-list">
        <div class="cards">
          <template v-if="!error && !loading">
            <wellness-video-card
              v-for="wellnessVideo in wellnessVideos"
              :key="wellnessVideo.id"
              :wellness-video="wellnessVideo"
              :show-expander="false"
            />
          </template>
        </div>
      </div>

      <!-- MOBILE VIEW -->
      <div class="group -full mobile-carousel">
        <carousel
          :per-page="1"
          pagination-color="#B3BCCB"
          pagination-active-color="#000000"
          :value="currentPage"
          :pagination-padding="7"
          :pagination-size="5"
          :navigation-enabled="false"
          :navigation-click-target-size="5"
          @touchstart="swipeStart"
          @touchmove="swipeMove"
          @touchend="swipeEnd"
        >
          <slide
            v-for="wellnessVideo in wellnessVideos"
            :key="wellnessVideo.id"
            class="slide-center"
          >
            <wellness-video-card :wellness-video="wellnessVideo" :show-expander="false" />
          </slide>
        </carousel>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { Carousel, Slide } from '@jambonn/vue-concise-carousel'
import Callout from '@/components/notifications/callout.vue'
import { useSwipe } from '@/composables/swipe'
import SpinnerLoader from '@/components/loaders/spinner-loader.vue'
import WellnessVideoCard from '@/components/wellness-videos/wellness-video-card.vue'
import { fbApi } from '@/utils/http-api'
import type { SearchTeamMember } from '@/types/search-types'
import type { WellnessVideoCardType } from '@/types/model-types'

/** PROPS **/
const props = defineProps<{
  teamMember: SearchTeamMember
}>()

/** STATE **/
const { swipeStart, swipeMove, swipeEnd } = useSwipe()
const error = ref('')
const loading = ref(false)
const wellnessVideos = ref<WellnessVideoCardType[]>([])
const sortMethod = ref('newest')
const currentPage = ref(0)

/** LIFECYCLE **/
onMounted(() => {
  fetchWellnessVideos()
})

/** METHODS **/
async function fetchWellnessVideos() {
  if (loading.value) {
    return
  }

  loading.value = true
  error.value = ''

  const url = `/wellness-videos?teammember%5B%5D=${props.teamMember.id}&sort=${sortMethod.value}`

  try {
    const res = await fbApi.get(url)

    if (!res || !res.body || !res.body.data) {
      throw new Error(`Unable to fetch wellness videos, please refresh the page and try again`)
    }

    loading.value = false
    error.value = ''

    wellnessVideos.value = res.body.data.paginatedWellnessVideos.data.slice(0, 4)
  } catch (err) {
    loading.value = false
    error.value = `Unable to fetch wellness videos, please refresh the page and try again`
  }
}
</script>
